import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNavbar from "../components/layout/PublicNavbar";
import PublicFooter from "../components/layout/PublicFooter";
import CampusCard from "../components/campus/CampusCard";
import ProcessSteps from "../components/campus/ProcessSteps";
import ApplicationForm from "../components/student/ApplicationForm";
import Loader from "../components/common/Loader";
import EmptyState from "../components/common/EmptyState";
import ErrorState from "../components/common/ErrorState";
import universityService from "../services/universityService";
import { useSettings } from "../hooks/useSettings";
import { useStudentAuth } from "../context/StudentAuthContext";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const WHO_WE_ARE_STATS = [
  { value: "2,500+", label: "Leaders hired" },
  { value: "1,000+", label: "Clients served" },
  { value: "50+", label: "Countries hired into" },
  { value: "80+", label: "Countries sourced from" },
];

const DIFFERENTIATORS = [
  {
    icon: "◈",
    title: "Disruptive search methodology",
    points: [
      "Research-based, not network-based — access to a far larger universe of talent",
      "A problem-solving approach across synergistic sectors",
      "Building capability in the disruptive ecosystem, hiring across sectors",
    ],
  },
  {
    icon: "◎",
    title: "Seamless cross-border platform",
    points: [
      "A global platform that sources talent seamlessly across geographies",
      "We stretch the boundaries of the target talent universe",
      "Creative, out-of-the-box thinking at every stage of a search",
    ],
  },
  {
    icon: "◐",
    title: "Diverse global talent pools",
    points: [
      "Global reach and deep research capability",
      "A deliberate focus on skill, geography, culture, sector and gender diversity",
      "Creative, diverse talent solutions delivered across sectors",
    ],
  },
];

const STORY_TIMELINE = [
  {
    years: "1992",
    title: "Start of an inspiring journey",
    points: [
      "Founded by two young management graduates with entrepreneurial zeal",
      "HQ in Delhi NCR — pioneering unique talent acquisition strategies",
    ],
  },
  {
    years: "1992–2002",
    title: "The decade of expansion",
    points: [
      "A pivotal role in the evolution of executive search in South Asia",
      "Mumbai & Bangalore offices set up",
      "Global key accounts built through a cross-border, cross-cultural search model",
    ],
  },
  {
    years: "2002–2012",
    title: "The decade of exponential growth",
    points: [
      "Only Indian-origin search firm to go global — Singapore, London, Paris, Hong Kong",
      "70% of business revenue from global clientele",
      "First venture capital investment, in big-data company Positive Integers",
    ],
  },
  {
    years: "2012–2026",
    title: "The decade of discovering new horizons",
    points: [
      "Offices set up in New York and Dubai",
      "Talent hired across 6 continents",
      "New consulting lines added: organisation design, deconstruction, public sector",
      "Ventured into more investments like Rapido, Stashfin, etc.",
    ],
  },
];

const TRAITS = [
  { icon: "◆", title: "Research & analytical capability", desc: "Understands global trends and derives meaningful insights from market analysis and talent trends." },
  { icon: "△", title: "Strong intellect", desc: "Shows a high degree of learning agility, reasoning and understanding of the deliverables." },
  { icon: "◇", title: "Communication skills", desc: "Expresses views and thoughts in a clear, concise and logical manner, verbally and in writing." },
  { icon: "▲", title: "Initiative, drive & resourcefulness", desc: "Works with a high degree of autonomy, takes on challenging assignments, and has superior teamwork skills." },
  { icon: "●", title: "Result orientation", desc: "Achieves results against defined objectives with creativity, innovation and resourcefulness." },
];

const PERKS = [
  "Network with global C-suite leaders",
  "Engage with global corporate clients",
  "Mentorship by senior partners",
  "Build deep domain capability",
  "Develop a consulting & solution-oriented mindset",
];

export default function CampusHiring() {
  const { settings } = useSettings();
  const { isAuthenticated, account } = useStudentAuth();
  const [campuses, setCampuses] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | empty | error
  const [selectedCampus, setSelectedCampus] = useState("");

  function load() {
    setState("loading");
    universityService
      .publicActive()
      .then((res) => {
        setCampuses(res.data || []);
        setState((res.data || []).length ? "ready" : "empty");
      })
      .catch(() => setState("error"));
  }

  useEffect(load, []);

  function scrollToApply(campus) {
    setSelectedCampus(campus.name);
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="pm-public">
      <PublicNavbar />

      {/* HERO */}
      <section className="pm-hero">
        <div className="pm-hero__glow" />
        <motion.div
          className="pm-hero__float pm-hero__float--a"
          animate={{ y: [0, -18, 0] }}
          transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
        />
        <motion.div
          className="pm-hero__float pm-hero__float--b"
          animate={{ y: [0, 16, 0] }}
          transition={{ repeat: Infinity, duration: 9, ease: "easeInOut" }}
        />

        <div className="pm-hero__inner">
          <div className="pm-hero__copy">
            <motion.span
              className="pm-eyebrow"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              ✦ {settings?.name || "Positive Moves"} · Campus Hiring
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              Transforming the talent ecosystem,
              <br />
              <em>redefining futures</em>
              <br />
              since 1992.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              Work alongside senior partners on live, C-suite mandates across 80+
              countries — and build a consulting career from day one.
            </motion.p>

            <motion.div
              className="pm-hero__cta"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <a href="#apply" className="pm-btn pm-btn--primary pm-btn--lg">
                Start your application →
              </a>
              <a
                href="https://www.positivemoves.com"
                target="_blank"
                rel="noreferrer"
                className="pm-btn pm-btn--ghost pm-btn--lg"
              >
                Visit positivemoves.com
              </a>
            </motion.div>

            <motion.div
              className="pm-hero__meta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div>
                <span>Gurugram / Mumbai</span>
                <small>Location</small>
              </div>
              <div>
                <span>Since 1992</span>
                <small>Global executive search</small>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="pm-hero__visual"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="pm-hero__panel">
              <p className="pm-hero__panel-quote">
                We are the trusted global talent partner, <span>building capabilities</span>,
                creating value and making an impact.
              </p>
              <div className="pm-hero__panel-stats">
                <div>
                  <strong>2,500+</strong>
                  <span>Leaders hired</span>
                </div>
                <div>
                  <strong>80+</strong>
                  <span>Countries covered</span>
                </div>
              </div>
              <div className="pm-hero__panel-foot">
                <span>New York · London · Paris</span>
                <span>Dubai · Singapore · Mumbai</span>
              </div>
            </div>
            <div className="pm-hero__badge">
              <strong>Est. 1992</strong>
              Only Indian-origin search firm to go global
            </div>
          </motion.div>
        </div>
      </section>

      {/* THE INDUSTRY */}
      <motion.section
        className="pm-section pm-industry"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
      >
        <motion.span className="pm-eyebrow pm-eyebrow--dark" variants={fadeUp}>The industry</motion.span>
        <motion.h2 variants={fadeUp}>What is executive search?</motion.h2>
        <span className="pm-heading-rule" />
        <div className="pm-industry__grid">
          <motion.div className="pm-industry__panel" variants={fadeUp}>
            Executive search is the niche practice of attracting highly skilled senior
            executive talent — largely <strong>CXO and CXO-1 level mandates</strong> —
            across sectors and regions, including market-leading and hard-to-fill roles.
          </motion.div>
          <motion.div className="pm-industry__panel" variants={fadeUp}>
            The global executive search market is estimated at approximately{" "}
            <span className="pm-highlight">USD 64 billion in 2026</span>, up from USD 58
            billion in 2025, and is projected to exceed USD 100 billion by 2031 — driven by
            demand for specialised leadership and new C-suite capabilities.
          </motion.div>
        </div>
      </motion.section>

      {/* WHO WE ARE */}
      <motion.section
        id="about"
        className="pm-section pm-about"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
      >
        <motion.span className="pm-eyebrow" variants={fadeUp}>Who we are</motion.span>
        <motion.h2 variants={fadeUp}>A truly global, diverse and purpose-driven firm.</motion.h2>
        <span className="pm-heading-rule" />
        <motion.p variants={fadeUp} className="pm-lead">
          Founded in 1992 by two young management graduates, Positive Moves is the only
          Indian-origin search firm to go global. Our offices span New York, London, Paris,
          Dubai, Singapore, Hong Kong, Mumbai and New Delhi.
        </motion.p>

        <div className="pm-whoweare__stats">
          {WHO_WE_ARE_STATS.map((s) => (
            <motion.div className="pm-whoweare__stat" key={s.label} variants={fadeUp}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* WHAT SETS US APART */}
      <motion.section
        className="pm-section pm-work"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
      >
        <motion.span className="pm-eyebrow" variants={fadeUp}>What sets us apart</motion.span>
        <motion.h2 variants={fadeUp}>An unmatched read on <span className="pm-nowrap">global talent.</span></motion.h2>
        <span className="pm-heading-rule" />
        <div className="pm-diff-grid">
          {DIFFERENTIATORS.map((d) => (
            <motion.div className="pm-diff-card" key={d.title} variants={fadeUp}>
              <div className="pm-diff-card__icon">{d.icon}</div>
              <h4>{d.title}</h4>
              <ul>
                {d.points.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* OUR STORY */}
      <motion.section
        className="pm-section pm-story"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
      >
        <motion.span className="pm-eyebrow pm-eyebrow--dark" variants={fadeUp}>Our story</motion.span>
        <motion.h2 variants={fadeUp}>A story of phenomenal growth.</motion.h2>
        <span className="pm-heading-rule" />
        <div className="pm-timeline">
          {STORY_TIMELINE.map((t) => (
            <motion.div className="pm-timeline__item" key={t.years} variants={fadeUp}>
              <div className="pm-timeline__years">{t.years}</div>
              <div>
                <h4>{t.title}</h4>
                <ul>
                  {t.points.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* WHAT WE'RE LOOKING FOR */}
      <motion.section
        className="pm-section pm-traits"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
      >
        <motion.span className="pm-eyebrow" variants={fadeUp}>What we're looking for</motion.span>
        <motion.h2 variants={fadeUp} className="pm-nowrap-heading">People who spot what others miss.</motion.h2>
        <span className="pm-heading-rule" />
        <div className="pm-trait-grid">
          {TRAITS.map((t) => (
            <motion.div className="pm-trait-row" key={t.title} variants={fadeUp}>
              <div className="pm-trait-row__icon">{t.icon}</div>
              <div>
                <h4>{t.title}</h4>
                <p>{t.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* WHAT'S IN IT FOR YOU */}
      <motion.section
        className="pm-section pm-perks"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
      >
        <motion.span className="pm-eyebrow pm-eyebrow--dark" variants={fadeUp}>What's in it for you</motion.span>
        <motion.h2 variants={fadeUp}>More than a line on your resume.</motion.h2>
        <span className="pm-heading-rule" />
        <div className="pm-perks-grid">
          {PERKS.map((p) => (
            <motion.div className="pm-perk-pill" key={p} variants={fadeUp}>{p}</motion.div>
          ))}
        </div>
      </motion.section>

      {/* CAMPUS OPPORTUNITIES */}
      <section id="opportunities" className="pm-section pm-opportunities">
        <span className="pm-eyebrow">Campus opportunities</span>
        <h2>Open campuses right now</h2>
        <span className="pm-heading-rule" />
        <p className="pm-lead" style={{ marginTop: 20 }}>
          These campuses currently have an active hiring window. Select one to prefill
          your application below.
        </p>

        {state === "loading" && <Loader label="Loading open campuses" />}
        {state === "error" && <ErrorState onRetry={load} message="Couldn't load open campuses." />}
        {state === "empty" && (
          <EmptyState
            title="No campuses are open right now"
            description="Check back soon, or submit a general application below."
          />
        )}
        {state === "ready" && (
          <div className="pm-campus-grid">
            {campuses.map((c, i) => (
              <CampusCard key={c.id} campus={c} index={i} onApply={scrollToApply} />
            ))}
          </div>
        )}
      </section>

      {/* PROCESS */}
      <section id="process" className="pm-section pm-process-section">
        <span className="pm-eyebrow">Selection process</span>
        <h2>Four steps. No mystery.</h2>
        <span className="pm-heading-rule" />
        <div style={{ marginTop: 44 }}>
          <ProcessSteps />
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section id="eligibility" className="pm-section pm-eligibility">
        <div className="pm-eligibility__card">
          <span className="pm-eyebrow pm-eyebrow--onDark">Eligibility</span>
          <h2>Who should apply</h2>
          <ul>
            <li>First year students at a participating campus</li>
            <li>Comfortable with ambiguity and quick, structured thinking</li>
          {/*  <li>A short intro video that shows how you communicate, not just what you say</li> */}
          </ul>
        </div>
      </section>

      {/* APPLY */}
      <section id="apply" className="pm-section pm-apply">
        <span className="pm-eyebrow">Application</span>
        <h2>Start your application</h2>
        <span className="pm-heading-rule" />
        <p className="pm-lead" style={{ marginTop: 20 }}>
          Takes about five minutes. Have your CV and a short intro video ready.
        </p>
        <div className="pm-apply__form-wrap">
          {isAuthenticated ? (
            <>
              <div className="pm-apply__signed-in">
                Applying as <strong>{account?.full_name}</strong> ({account?.email}) ·{" "}
                <Link to="/student/dashboard">Go to my applications</Link>
              </div>
              <ApplicationForm campuses={campuses} defaultCampus={selectedCampus} />
            </>
          ) : (
            <div className="pm-apply__gate">
              <h3>Log in to apply</h3>
              <p>
                One account lets you apply to any open campus and track every
                application's status in one place.
              </p>
              <div className="pm-apply__gate-cta">
                <Link
                  to="/student/signup"
                  state={{ from: { pathname: "/" } }}
                  className="pm-btn pm-btn--primary pm-btn--lg"
                >
                  Create an account
                </Link>
                <Link
                  to="/student/login"
                  state={{ from: { pathname: "/" } }}
                  className="pm-btn pm-btn--outline pm-btn--lg"
                >
                  Log in
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
